import React, { createContext, useContext, useState, ReactNode } from 'react'
import type {
  TransportScores,
  PublicTransportMetrics,
  RoadAccessMetrics,
  ActiveMobilityMetrics,
  TransportServices,
} from './types'

interface TransportAndAccessContextType {
  // Services
  services: TransportServices
  // Data states
  scores: TransportScores | null
  publicTransportMetrics: PublicTransportMetrics | null
  roadAccessMetrics: RoadAccessMetrics | null
  activeMobilityMetrics: ActiveMobilityMetrics | null
  isochrones: Record<string, any> | null
  publicTransportStops: any[]

  // Loading states
  isLoadingScores: boolean
  isLoadingIsochrones: boolean
  isLoadingStops: boolean

  // Error states
  error: string | null

  // Selected view
  activeView: 'overview' | 'public-transport' | 'ev-charging'
  setActiveView: (view: 'overview' | 'public-transport' | 'ev-charging') => void

  // Map settings
  showIsochrones: boolean
  setShowIsochrones: (show: boolean) => void
  selectedTransportMode: 'walking' | 'cycling' | 'driving' | 'all'
  setSelectedTransportMode: (
    mode: 'walking' | 'cycling' | 'driving' | 'all'
  ) => void

  // Update functions
  setScores: (scores: TransportScores | null) => void
  setPublicTransportMetrics: (metrics: PublicTransportMetrics | null) => void
  setRoadAccessMetrics: (metrics: RoadAccessMetrics | null) => void
  setActiveMobilityMetrics: (metrics: ActiveMobilityMetrics | null) => void
  setIsochrones: (isochrones: Record<string, any> | null) => void
  setPublicTransportStops: (stops: any[]) => void
  setIsLoadingScores: (loading: boolean) => void
  setIsLoadingIsochrones: (loading: boolean) => void
  setIsLoadingStops: (loading: boolean) => void
  setError: (error: string | null) => void
}

const TransportAndAccessContext = createContext<
  TransportAndAccessContextType | undefined
>(undefined)

export const useTransportAndAccess = () => {
  const context = useContext(TransportAndAccessContext)
  if (!context) {
    throw new Error(
      'useTransportAndAccess must be used within TransportAndAccessProvider'
    )
  }
  return context
}

interface TransportAndAccessProviderProps {
  children: ReactNode
  services: TransportServices
}

export const TransportAndAccessProvider: React.FC<
  TransportAndAccessProviderProps
> = ({ children, services }) => {
  // Data states
  const [scores, setScores] = useState<TransportScores | null>(null)
  const [publicTransportMetrics, setPublicTransportMetrics] =
    useState<PublicTransportMetrics | null>(null)
  const [roadAccessMetrics, setRoadAccessMetrics] =
    useState<RoadAccessMetrics | null>(null)
  const [activeMobilityMetrics, setActiveMobilityMetrics] =
    useState<ActiveMobilityMetrics | null>(null)
  const [isochrones, setIsochrones] = useState<Record<string, any> | null>(null)
  const [publicTransportStops, setPublicTransportStops] = useState<any[]>([])

  // Loading states
  const [isLoadingScores, setIsLoadingScores] = useState(false)
  const [isLoadingIsochrones, setIsLoadingIsochrones] = useState(false)
  const [isLoadingStops, setIsLoadingStops] = useState(false)

  // Error state
  const [error, setError] = useState<string | null>(null)

  // UI states
  const [activeView, setActiveView] = useState<
    'overview' | 'public-transport' | 'ev-charging'
  >('overview')
  const [showIsochrones, setShowIsochrones] = useState(true)
  const [selectedTransportMode, setSelectedTransportMode] = useState<
    'walking' | 'cycling' | 'driving' | 'all'
  >('all')

  const value: TransportAndAccessContextType = {
    // Services
    services,

    // Data
    scores,
    publicTransportMetrics,
    roadAccessMetrics,
    activeMobilityMetrics,
    isochrones,
    publicTransportStops,

    // Loading
    isLoadingScores,
    isLoadingIsochrones,
    isLoadingStops,

    // Error
    error,

    // UI
    activeView,
    setActiveView,
    showIsochrones,
    setShowIsochrones,
    selectedTransportMode,
    setSelectedTransportMode,

    // Update functions
    setScores,
    setPublicTransportMetrics,
    setRoadAccessMetrics,
    setActiveMobilityMetrics,
    setIsochrones,
    setPublicTransportStops,
    setIsLoadingScores,
    setIsLoadingIsochrones,
    setIsLoadingStops,
    setError,
  }

  return (
    <TransportAndAccessContext.Provider value={value}>
      {children}
    </TransportAndAccessContext.Provider>
  )
}
